import React, { useEffect, useRef, useState } from 'react';

import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import Modal from '../../modules/Modal';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  deleteCollaborativeFolderById,
  deleteFolderByIdFromAllContentPage,
  getFolderDeleteImpact,
  getFolderDeleteImpactFromAllContentPage,
} from '../../../api/foldersApi';
import styles from './DeleteCollaborativeFolderModal.module.scss';
import { getUserOrganization } from '../../../api';
import { saveMyOrganization } from '../../../store/user/userSlice';
import { SortOptions, SortType } from '../../../enums/SORT';
import { useHotkeys } from 'react-hotkeys-hook';

type TDeleteCollaborativeFolderModal = {
  open: boolean;
  switchModal: () => void;
  folderId: string;
  data: { page: number; limit: number; search?: string; sort?: SortType; sortOption: SortOptions };
  fetchCallBack: () => void;
};

const DeleteCollaborativeFolderModal: React.FC<TDeleteCollaborativeFolderModal> = ({
  open,
  switchModal,
  folderId,
  data,
  fetchCallBack,
}) => {
  const [loading, setLoading] = useState(false);
  const [impactLoading, setImpactLoading] = useState(false);
  const [impactedComposers, setImpactedComposers] = useState<string[]>([]);
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const onModalClose = () => {
    switchModal();
  };
  const dispatch = useAppDispatch();
  const { myOrganization } = useAppSelector((state) => state.user);

  const handleLocalStorageKeysForResumeUploading = () => {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        if (key && key.startsWith('=videofeedToResume=') && key.includes(`=${folderId}::`)) {
          console.log('2');
          localStorage.removeItem(key);
        }
      }
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      handleLocalStorageKeysForResumeUploading();
      if (myOrganization.type === 'parent') {
        await deleteCollaborativeFolderById(folderId);
      } else {
        await deleteFolderByIdFromAllContentPage(folderId);
      }
      onModalClose();

      fetchCallBack();
      getUserOrganization()
        .then((resp) => {
          dispatch(saveMyOrganization(resp.data));
        })
        .catch((error) => console.error(error));
    } catch (err) {
      console.log(err);
    } finally {
      onModalClose();
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    if (!open || !folderId) return;

    setImpactLoading(true);
    setImpactedComposers([]);

    const impactRequest =
      myOrganization.type === 'parent'
        ? getFolderDeleteImpact(folderId)
        : getFolderDeleteImpactFromAllContentPage(folderId);

    impactRequest
      .then((response) => {
        if (!active) return;
        const names = Array.isArray(response?.data) ? response.data : [];
        setImpactedComposers(
          [...new Set(names.map((name: string) => String(name || '').trim()).filter(Boolean))],
        );
      })
      .catch(() => {
        if (active) setImpactedComposers([]);
      })
      .finally(() => {
        if (active) setImpactLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, folderId]);

  useHotkeys('Enter', () => {
    if (open && !loading) submitButtonRef?.current?.click();
  });

  return (
    <Modal open={open} onClose={onModalClose} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalTitle}>Are you sure you want to delete this folder?</div>
        {!impactLoading && !!impactedComposers.length && (
          <>
            <div className={styles.modalSubtitle}>
              Deleting this folder will also remove clips from these Online Composers. Composers that only depend on this source will be deleted:
            </div>
            <div className={styles.namesList}>
              {impactedComposers.map((name) => (
                <div key={name}>{name}</div>
              ))}
            </div>
          </>
        )}
        <div className={styles.form}>
          <div className={styles.actionBtns}>
            <OutlinedButton onClick={handleDelete} kind="default" disabled={loading || impactLoading}>
              <div ref={submitButtonRef}></div>Yes, I am sure
            </OutlinedButton>

            <ContainedButton kind="purple" onClick={onModalClose} disabled={loading || impactLoading}>
              No, cancel
            </ContainedButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteCollaborativeFolderModal;
